class MainStoreLocator extends HTMLElement {
	constructor() {
		super();
		this.locations = null;
		this.map = null;
		this.popup = null;
		this._focusSeq = 0;
		this._scrollRaf = null;
		this._moveFallback = null;
		this._origFeatures = null;
	}

	connectedCallback() {
		const list = this.querySelector(".locations");
		const mapEl = this.querySelector(".map-container");
		if (list) {
			const h = (mapEl?.clientHeight || 500) + "px";
			list.style.maxHeight = h;
			list.style.overflowY = "auto";
		}
		this.initLocations();

		// Search handler
		document.getElementById("locator-search-btn")?.addEventListener("click", async () => {
			const center = await this._getSearchCenter();
			const radiusKm = Number(document.getElementById("locator-distance-select").value) || 5;
			if (center) {
				this.filterAndDisplayStores(center, radiusKm);
			} else {
				// Không tìm thấy geocode → trả về ALL STORE như trạng thái ban đầu!
				this._resetFilter();
			}
		});

		// Reset handler
		document.getElementById("locator-reset-btn")?.addEventListener("click", () => {
			this._resetFilter();
		});
	}

	async initLocations() {
		const urlNode = this.querySelector("#locations-url");
		if (!urlNode) return;
		const url = JSON.parse(urlNode.textContent || '""');
		if (!url) return;
		try {
			const res = await fetch(url);
			if (!res.ok) throw new Error(`HTTP ${res.status}`);
			this.locations = await res.json();
			(this.locations.features || []).forEach((f, i) => {
				if (!f.properties) f.properties = {};
				f.properties.__uid = i;
			});
			this._origFeatures = [...this.locations.features]; // backup
			this.initMap();
			this.renderFilteredLocationsList(this.locations.features);
		} catch (e) {
			console.error("Error loading locations.json:", e);
		}
	}

	// ---- FILTER & RADIUS ----
	async _getSearchCenter() {
		const input = document.getElementById("locator-search-input").value.trim();
		if (!input) return null;
		const token = this.dataset.key;

		if (!token) {
			console.error("Mapbox token is not set");
			return;
		}

		const q = encodeURIComponent(input);
		try {
			const res = await fetch(
				`https://api.mapbox.com/geocoding/v5/mapbox.places/${q}.json?country=ca,us&limit=1&access_token=${token}`
			);
			const data = await res.json();
			return data?.features?.[0]?.center || null; // [lng,lat]
		} catch {
			return null;
		}
	}

	filterAndDisplayStores(center, radiusKm) {
		// 1. Filter store trong radius
		const filtered = (this._origFeatures || []).filter(
			(f) =>
				turf.distance(f.geometry.coordinates, center, { units: "kilometers" }) <= radiusKm
		);
		// Nếu không có kết quả, báo UI và cho phép reset
		document.getElementById("locator-no-results").style.display = filtered.length
			? "none"
			: "block";
		document.getElementById("locator-reset-btn").style.display = filtered.length
			? "none"
			: "inline-block";
		// 2. Render lại list
		this.renderFilteredLocationsList(filtered);
		// 3. Update marker
		if (this.map.getSource("locations")) {
			this.map.getSource("locations").setData({
				...this.locations,
				features: filtered,
			});
		}
		// 4. Vẽ radius
		this.drawRadiusCircle(center, radiusKm);
		// 5. Focus map vào vùng tìm kiếm
		this.map.flyTo({ center, zoom: 10 });
	}

	_resetFilter() {
		document.getElementById("locator-no-results").style.display = "none";
		document.getElementById("locator-reset-btn").style.display = "none";
		if (this.map.getLayer("radius-circle-fill")) this.map.removeLayer("radius-circle-fill");
		if (this.map.getSource("radius-circle")) this.map.removeSource("radius-circle");
		if (this._origFeatures) {
			this.renderFilteredLocationsList(this._origFeatures);
		}
		if (this.map.getSource("locations")) {
			this.map.getSource("locations").setData({
				...this.locations,
				features: this._origFeatures,
			});
		}
		// Optionally: fit lại map về toàn bộ marker
		const bounds = this._getGeoJsonBounds({ features: this._origFeatures });
		if (bounds) this.map.fitBounds(bounds, { padding: 40, duration: 600 });
	}

	drawRadiusCircle(center, radiusKm) {
		if (this.map.getLayer("radius-circle-fill")) this.map.removeLayer("radius-circle-fill");
		if (this.map.getSource("radius-circle")) this.map.removeSource("radius-circle");
		const circle = turf.circle(center, radiusKm, { steps: 80, units: "kilometers" });
		this.map.addSource("radius-circle", { type: "geojson", data: circle });
		this.map.addLayer({
			id: "radius-circle-fill",
			type: "fill",
			source: "radius-circle",
			paint: { "fill-color": "#237bcf", "fill-opacity": 0.22 },
		});
	}

	renderFilteredLocationsList(features) {
		const list = this.querySelector(".locations");
		list.innerHTML = "";
		features.forEach((feature) => {
			const frag = this.querySelector("#location-item-template").content.cloneNode(true);
			const root = frag.querySelector(".location-item") || frag.firstElementChild;
			root.dataset.uid = feature.properties.__uid;
			const title = `${feature.properties["Store Name"] || "Store"}, ${
				feature.properties["Address"] || ""
			}`;
			(frag.querySelector(".location-item-name") || root).textContent = title;
			root.addEventListener("click", (e) => {
				e.preventDefault();
				e.stopPropagation();
				this._focusFeature(feature.properties.__uid, { openPopup: "auto", smooth: true });
			});
			list.appendChild(frag);
		});
	}

	// ---- MAP + MARKER + POPUP ----

	initMap() {
		mapboxgl.accessToken = this.dataset.key;
		if (!mapboxgl.accessToken) {
			console.error("Mapbox token is not set");
			return;
		}
		const container = this.querySelector(".map-container");
		if (!container) return console.error("Không thấy .map-container");
		const STYLES = {
			map: "mapbox://styles/mapbox/streets-v11",
			sat: "mapbox://styles/mapbox/satellite-streets-v11",
		};
		this.map = new mapboxgl.Map({
			container,
			style: STYLES.map,
			center: [0, 0],
			zoom: 1,
		});
		window.mapboxMap = this.map; // Debug/truy cập ngoài class
		this.map.addControl(new mapboxgl.NavigationControl(), "top-right");
		const onClickMarker = (e) => {
			const feature = e.features?.[0];
			if (!feature) return;
			const uid = Number(feature.properties.__uid);
			this._focusFeature(uid, { openPopup: "auto", smooth: true });
		};
		const onEnterMarker = () => (this.map.getCanvas().style.cursor = "pointer");
		const onLeaveMarker = () => (this.map.getCanvas().style.cursor = "");
		const buildDataLayers = () => {
			if (!this.map.getSource("locations")) {
				this.map.addSource("locations", { type: "geojson", data: this.locations });
			}
			const addLayerNow = () => {
				if (!this.map.getLayer("locations")) {
					this.map.addLayer({
						id: "locations",
						type: "symbol",
						source: "locations",
						layout: {
							"icon-image": "custom-marker",
							"icon-size": 2,
							"icon-allow-overlap": true,
							"icon-anchor": "bottom",
						},
					});
					this.map.moveLayer("locations");
				}
				this.map.off("click", "locations", onClickMarker);
				this.map.off("mouseenter", "locations", onEnterMarker);
				this.map.off("mouseleave", "locations", onLeaveMarker);
				this.map.on("click", "locations", onClickMarker);
				this.map.on("mouseenter", "locations", onEnterMarker);
				this.map.on("mouseleave", "locations", onLeaveMarker);
				const bounds = this._getGeoJsonBounds(this.locations);
				if (bounds) this.map.fitBounds(bounds, { padding: 40, duration: 0 });
			};
			const iconUrl = this.dataset.iconUrl;
			if (!iconUrl) {
				if (!this.map.getLayer("locations")) {
					this.map.addLayer({
						id: "locations",
						type: "circle",
						source: "locations",
						paint: { "circle-radius": 6, "circle-color": "#000" },
					});
				}
				this.map.off("click", "locations", onClickMarker);
				this.map.on("click", "locations", onClickMarker);
				const bounds = this._getGeoJsonBounds(this.locations);
				if (bounds) this.map.fitBounds(bounds, { padding: 40, duration: 0 });
				return;
			}
			this.map.loadImage(iconUrl, (err, img) => {
				if (err || !img) {
					const htmlImg = new Image();
					htmlImg.crossOrigin = "anonymous";
					htmlImg.src = iconUrl;
					htmlImg.onload = () => {
						if (!this.map.hasImage("custom-marker")) {
							this.map.addImage("custom-marker", htmlImg, { pixelRatio: 2 });
						}
						addLayerNow();
					};
					return;
				}
				if (!this.map.hasImage("custom-marker")) {
					this.map.addImage("custom-marker", img, { pixelRatio: 2 });
				}
				addLayerNow();
			});
		};
		const styleToggleControl = {
			onAdd: (map) => {
				const ctrl = document.createElement("div");
				ctrl.className = "mapboxgl-ctrl mapboxgl-ctrl-group style-toggle-ctrl";
				const btnMap = document.createElement("button");
				btnMap.type = "button";
				btnMap.textContent = "MAP";
				const btnSat = document.createElement("button");
				btnSat.type = "button";
				btnSat.textContent = "SATELLITE";
				const setActiveBtn = (mode) => {
					btnMap.classList.toggle("active", mode === "map");
					btnSat.classList.toggle("active", mode === "sat");
				};
				const switchStyle = (mode) => {
					const target = mode === "sat" ? STYLES.sat : STYLES.map;
					setActiveBtn(mode);
					map.setStyle(target);
					map.once("style.load", () => buildDataLayers());
				};
				btnMap.addEventListener("click", () => switchStyle("map"));
				btnSat.addEventListener("click", () => switchStyle("sat"));
				setActiveBtn("map");
				ctrl.appendChild(btnMap);
				ctrl.appendChild(btnSat);
				return ctrl;
			},
			onRemove: () => {},
		};
		this.map.addControl(styleToggleControl, "top-left");
		this.map.on("load", () => buildDataLayers());
	}

	// ===== Helpers =====

	_focusFeature(uid, { openPopup = "auto", smooth = true } = {}) {
		const f = this.locations?.features?.find((f) => f.properties?.__uid === uid);
		if (!f) return;
		let [lng, lat] = f.geometry?.coordinates || [];
		if (lng == null || lat == null) return;
		const cur = this.map.getCenter ? this.map.getCenter() : { lng: 0, lat: 0 };
		lng = this._wrapToNearestWorld(lng, cur.lng);
		const target = [lng, lat];
		const openPopupNow = () => {
			this._openPopup(target, f.properties, { closeOnMove: false });
			this._setActiveListItem(uid);
			this._scrollItemIntoView(uid);
		};
		this.map?.stop();
		const curZoom = this.map.getZoom ? this.map.getZoom() || 1 : 1;
		const targetZoom = Math.max(curZoom, 12);
		const centerDelta = Math.hypot(cur.lng - lng || 0, cur.lat - lat || 0);
		const zoomDelta = Math.abs(curZoom - targetZoom);
		const needMove = smooth && (centerDelta > 1e-4 || zoomDelta > 0.01);
		if (needMove) {
			let cancelled = false;
			const onMoveEnd = () => {
				if (cancelled) return;
				this.map.off("moveend", onMoveEnd);
				openPopupNow();
			};
			this.map.on("moveend", onMoveEnd);
			this.map.easeTo({ center: target, zoom: targetZoom, duration: 500 });
			setTimeout(() => {
				cancelled = true;
				this.map.off("moveend", onMoveEnd);
				openPopupNow();
			}, 1100);
		} else {
			this.map.jumpTo({ center: target, zoom: targetZoom });
			openPopupNow();
		}
	}

	_openPopup(coords, props = {}, opts = {}) {
		try {
			this.popup?.remove();
		} catch {}
		this.popup = new mapboxgl.Popup({
			offset: [0, -50],
			closeOnMove: opts.closeOnMove ?? true,
		})
			.setLngLat(coords)
			.setHTML(
				`<strong>${props["Store Name"] || "Store"}</strong><br>${props["Address"] || ""}`
			)
			.addTo(this.map);
	}

	_freezeBodyX(on) {
		const html = document.documentElement;
		if (on) {
			this._savedOverflowX = html.style.overflowX;
			html.style.overflowX = "hidden";
		} else {
			html.style.overflowX = this._savedOverflowX ?? "";
			this._savedOverflowX = null;
		}
	}

	_setActiveListItem(uid) {
		const list = this.querySelector(".locations");
		if (!list) return;
		list.querySelectorAll(".location-item.is-active").forEach((el) =>
			el.classList.remove("is-active")
		);
		const el = list.querySelector(`.location-item[data-uid="${uid}"]`);
		if (el) el.classList.add("is-active");
	}

	_scrollItemIntoView(uid) {
		const list = this.querySelector(".locations");
		const el = list?.querySelector(`.location-item[data-uid="${uid}"]`);
		if (!list || !el) return;
		const top = el.offsetTop - (list.clientHeight / 2 - el.clientHeight / 2);
		if (this._scrollRaf) {
			cancelAnimationFrame(this._scrollRaf);
		}
		this._scrollRaf = requestAnimationFrame(() => {
			list.scrollTo({
				top: top,
				behavior: "smooth",
			});
		});
	}

	_wrapToNearestWorld(lng, refLng) {
		const d = lng - refLng;
		return refLng + ((((d + 180) % 360) + 360) % 360) - 180;
	}

	_getGeoJsonBounds(fc) {
		try {
			let minX = Infinity,
				minY = Infinity,
				maxX = -Infinity,
				maxY = -Infinity;
			(fc.features || []).forEach((f) => {
				if (f?.geometry?.type === "Point") {
					const [x, y] = f.geometry.coordinates;
					if (x < minX) minX = x;
					if (y < minY) minY = y;
					if (x > maxX) maxX = x;
					if (y > maxY) maxY = y;
				}
			});
			if (minX === Infinity) return null;
			return [
				[minX, minY],
				[maxX, maxY],
			];
		} catch {
			return null;
		}
	}
}

customElements.define("main-store-locator", MainStoreLocator);
